import React, { useState, useRef } from "react";
import "./indicator.css";

const LEVEL_LABELS = {
  1: "Emerging",
  2: "Developing",
  3: "Established",
  4: "Exemplary",
};

export default function IndicatorRow({
  indicator,
  score,
  evidence,
  attachments = [],
  onChange,
  onEvidenceChange,
  onAttachmentsChange,
  assessmentId,
}) {
  const recognitionRef = useRef(null);
  const listeningRef = useRef(false);
  const evidenceRef = useRef(evidence || "");
  const [showRubric, setShowRubric] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const selectedScore = score == null ? null : Number(score);

  const handleScore = (val) => {
    onChange(indicator.code, selectedScore === val ? null : val);
  };
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    if (!assessmentId) {
      alert("Please start the assessment before uploading evidence.");
      event.target.value = "";
      return;
    }

    setUploading(true);

    try {
      const uploadedFiles = [];

      for (const file of files) {
        const formData = new FormData();

        formData.append("file", file);
        formData.append("assessmentId", assessmentId);
        formData.append("indicator", indicator.code);
        formData.append("score", selectedScore);

        const apiBase = process.env.REACT_APP_API_URL
          ? process.env.REACT_APP_API_URL.replace(/\/$/, "")
          : "";

        const response = await fetch(
          `${apiBase}/api/uploads/evidence`,
          {
            method: "POST",
            body: formData,
          },
        );

        const responseText = await response.text();

        let data = {};

        try {
          data = responseText ? JSON.parse(responseText) : {};
        } catch (error) {
          console.error("Non-JSON response from upload API:", responseText);
        }

        if (!response.ok) {
          throw new Error(data.message || `File upload failed: ${file.name}`);
        }

        uploadedFiles.push({
          id: data.file.id,
          originalName: data.file.originalName,
          mimeType: data.file.mimeType,
          size: data.file.size,
          type: data.file.type,
          url: data.file.url,
          publicId: data.file.publicId,
        });
      }

      onAttachmentsChange(indicator.code, [...attachments, ...uploadedFiles]);

      console.log("Uploaded evidence attachments:", uploadedFiles);
    } catch (error) {
      console.error("File upload error:", error);
      alert(error.message || "Failed to upload file");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };
  const handleRemoveAttachment = (publicId) => {
    const updatedAttachments = attachments.filter(
      (file) => file.publicId !== publicId,
    );

    onAttachmentsChange(indicator.code, updatedAttachments);
  };
  evidenceRef.current = evidence || "";
  const handleVoiceInput = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechSupported(false);
      alert("Voice input is not supported by this browser.");
      return;
    }

    // Stop listening
    if (listeningRef.current) {
      listeningRef.current = false;
      setIsListening(false);

      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      return;
    }

    const recognition = new SpeechRecognition();

    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onstart = () => {
      listeningRef.current = true;
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let transcript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }

      if (!transcript.trim()) return;

      const currentText = evidenceRef.current.trim();

      const newText = currentText
        ? `${currentText} ${transcript.trim()}`
        : transcript.trim();

      evidenceRef.current = newText;

      onEvidenceChange(indicator.code, newText);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);

      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      ) {
        listeningRef.current = false;
        setIsListening(false);
        alert("Microphone permission was denied.");
      }
    };

    recognition.onend = () => {
      if (listeningRef.current) {
        try {
          recognition.start();
        } catch (error) {
          // Recognition may already be restarting.
        }
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  return (
    <div className={`indicator-row${indicator.isCore ? " is-core" : ""}`}>
      <div className="indicator-header">
        <div className="indicator-meta">
          <div className="indicator-code-name">
            <span className="indicator-code">{indicator.code}</span>
            <span className="indicator-name">{indicator.name}</span>
            {indicator.isCore && <span className="core-badge">★ CORE ×2</span>}
            {indicator.v4note && <span className="v4-badge">V5</span>}
          </div>
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              marginTop: "0.2rem",
              alignItems: "center",
            }}
          >
            <span className="herqa-badge">{indicator.herqaStd}</span>
            {indicator.v4note && (
              <span
                style={{
                  fontSize: "0.72rem",
                  color: "#7c3aed",
                  fontStyle: "italic",
                }}
              >
                {indicator.v4note}
              </span>
            )}
          </div>
        </div>

        <div className="score-selector">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setShowRubric((r) => !r)}
            title="Toggle rubric details"
            style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
          >
            {showRubric ? "▲ Rubric" : "▼ Rubric"}
          </button>
          {[1, 2, 3, 4].map((val) => (
            <button
              key={val}
              type="button"
              className={`score-btn${selectedScore === val ? ` selected-${val}` : ""}`}
              onClick={() => handleScore(val)}
              title={LEVEL_LABELS[val]}
            >
              {val}
            </button>
          ))}
          {selectedScore !== null && (
            <div
              style={{
                fontSize: "0.7rem",
                color: "var(--text-3)",
                textAlign: "center",
                minWidth: "50px",
              }}
            >
              <div style={{ fontWeight: 700, color: "var(--navy)" }}>
                {indicator.isCore ? selectedScore * 2 : selectedScore} pts
              </div>
              <div>{LEVEL_LABELS[selectedScore]}</div>
            </div>
          )}
        </div>
      </div>

      {showRubric && (
        <div className="rubric-grid">
          {[1, 2, 3, 4].map((lv) => (
            <div
              key={lv}
              className={`rubric-cell level-${lv}${score === lv ? " active" : ""}`}
              onClick={() => handleScore(lv)}
              style={{ cursor: "pointer" }}
            >
              <span className={`rubric-label rubric-label-${lv}`}>
                Level {lv} — {LEVEL_LABELS[lv]}
              </span>
              {indicator.levels[lv]}
            </div>
          ))}
        </div>
      )}

      {selectedScore !== null && (
        <div
          style={{
            marginTop: "0.9rem",
            padding: "0.9rem",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            background: "#f8fafc",
            position: "relative",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: 600,
            }}
          >
            Evidence
          </label>

          <textarea
            value={evidence || ""}
            onChange={(e) => {
              evidenceRef.current = e.target.value;
              onEvidenceChange(indicator.code, e.target.value);
            }}
            placeholder="Enter evidence supporting the selected score..."
            rows={3}
            style={{
              width: "100%",
              padding: "0.75rem",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              resize: "vertical",
            }}
          />

          <button
            type="button"
            title={isListening ? "Listening..." : "Write with your voice"}
            onClick={handleVoiceInput}
            disabled={uploading}
            style={{
              position: "absolute",
              left: "0.7rem",
              top: "5.55rem",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              cursor: uploading ? "not-allowed" : "pointer",
              color: isListening ? "#dc2626" : "#475569",
              background: isListening ? "#fef2f2" : "#ffffff",
              border: `1px solid ${isListening ? "#fecaca" : "#cbd5e1"}`,
              borderRadius: "7px",
              fontSize: "16px",
              lineHeight: 1,
              zIndex: 2,
              transition: "all 0.15s ease",
              boxShadow: isListening
                ? "0 0 0 3px rgba(220, 38, 38, 0.08)"
                : "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            {isListening && (
              <>
                <span className="voice-wave voice-wave-1" />
                <span className="voice-wave voice-wave-2" />
                <span className="voice-wave voice-wave-3" />
              </>
            )}
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <line x1="12" y1="19" x2="12" y2="22" />
              <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
          </button>
          <input
            id={`evidence-file-${indicator.code}`}
            type="file"
            multiple
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />

          <label
            htmlFor={`evidence-file-${indicator.code}`}
            title="Attach evidence"
            style={{
              position: "absolute",
              right: "1rem",
              top: "1.5rem",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: uploading ? "wait" : "pointer",
              color: "#475569",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "7px",
              fontSize: "16px",
              lineHeight: 1,
              opacity: uploading ? 0.5 : 1,
              zIndex: 2,
              transition: "all 0.15s ease",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => {
              if (!uploading) {
                e.currentTarget.style.color = "#1a365d";
                e.currentTarget.style.borderColor = "#94a3b8";
                e.currentTarget.style.background = "#f8fafc";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#475569";
              e.currentTarget.style.borderColor = "#cbd5e1";
              e.currentTarget.style.background = "#ffffff";
            }}
          >
            <span style={{ fontSize: "17px" }}>↗</span>
          </label>
          {attachments.length > 0 && (
            <div
              style={{
                marginTop: "0.75rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              {attachments.map((file) => {
                const extension =
                  file.originalName?.split(".").pop()?.toUpperCase() || "FILE";

                const size =
                  file.size < 1024 * 1024
                    ? `${Math.max(1, Math.round(file.size / 1024))} KB`
                    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;

                return (
                  <div
                    key={file.id || file.publicId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.65rem 0.75rem",
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      transition: "border-color 0.15s ease",
                    }}
                  >
                    {/* File icon */}
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        minWidth: "36px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "7px",
                        background: "#f1f5f9",
                        color: "#475569",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                      }}
                    >
                      {extension}
                    </div>

                    {/* File information */}
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "block",
                          color: "#1e293b",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          textDecoration: "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={file.originalName}
                      >
                        {file.originalName}
                      </a>

                      <div
                        style={{
                          marginTop: "0.15rem",
                          fontSize: "0.7rem",
                          color: "#94a3b8",
                        }}
                      >
                        {extension} · {size}
                      </div>
                    </div>

                    {/* Open */}
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open file"
                      style={{
                        width: "30px",
                        height: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "6px",
                        color: "#64748b",
                        textDecoration: "none",
                        fontSize: "15px",
                      }}
                    >
                      ↗
                    </a>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(file.publicId)}
                      title="Remove attachment"
                      style={{
                        width: "30px",
                        height: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        background: "transparent",
                        borderRadius: "6px",
                        cursor: "pointer",
                        color: "#94a3b8",
                        fontSize: "18px",
                        lineHeight: 1,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#dc2626";
                        e.currentTarget.style.background = "#fef2f2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#94a3b8";
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
